import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'i18n'
import { I18nComponentType } from 'i18n-types'
import ReactDOM from 'react-dom'

import { ButtonBox } from 'components/styled/button/base-button'
import { StyledButton } from 'components/styled/button/button'
import { Subtitle } from '../typography'
import { getActionBarRoot as getActionBarRootDefault } from '../page/app-content'
import { Button } from '../button'
import { MobileRatingButton } from '../rating/mobile'

export interface ActionBarProps {
  actions?: React.ReactNode
  useMobileMenu?: boolean
  selection?: {
    count: number
    text: string
  }
  leftSideComponent?: React.ReactNode
  getActionBarRoot?: () => HTMLElement | null
  hideActionBar?: boolean
}

const ActionBarWrapper = styled.div<{ setHidden?: boolean }>`
  background-color: ${({ theme }) => theme.color.background};

  ${({ theme }) => theme.mediaQuery.phone} {
    display: ${({ setHidden }) => (setHidden ? 'none' : 'block')};
  }
`

const StyledActionBar = styled.div`
  box-shadow: ${({ theme }) => theme.shadow.top};
  box-sizing: border-box;
  min-height: ${({ theme }) => theme.space.s64};
`

const LeftSide = styled.div`
  display: flex;

  ${({ theme }) => theme.mediaQuery.screenM} {
    padding-left: ${({ theme }) => theme.space.s12};
  }
`

const ActionBarContentWrapper = styled.div<{ $useMobileMenu: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  background-color: ${({ theme }) => theme.color.actionBarBackground};
  padding: ${({ theme }) => theme.space.s12} ${({ theme }) => theme.space.s48};

  ${({ theme }) => theme.mediaQuery.screenS} {
    flex-direction: column;
    gap: ${({ theme }) => theme.space.s12};
    align-items: stretch;
    padding-left: ${({ theme }) => theme.space.s16};
    padding-right: ${({ theme }) => theme.space.s16};
    padding-top: ${({ theme }) => theme.space.s20};
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: row;
    align-items: center;
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    ${({ $useMobileMenu }) =>
      !$useMobileMenu &&
      css`
        flex-direction: column;
        align-items: stretch;
      `}

    ${({ $useMobileMenu }) =>
      $useMobileMenu &&
      css`
        flex-direction: column-reverse;
        align-items: stretch;

        ${LeftSide} {
          padding-left: 0;
        }

        ${ButtonBox} {
          width: 100%;

          ${StyledButton} {
            width: 100%;
          }

          ${MobileRatingButton} {
            width: 100%;
          }
        }
      `}
  }
`

const LeftSideText = styled(LeftSide)`
  display: block;
  white-space: nowrap;
  ${({ theme }) => theme.mediaQuery.tablet} {
    white-space: break-spaces;
  }
`

const SelectionNumber = styled(Subtitle).attrs({
  fontWeight: 600,
})`
  display: inline;
  margin-bottom: 0;
  margin-right: ${({ theme }) => theme.space.s4};
`

const SelectionText = styled(Subtitle)`
  display: inline;
  margin-bottom: 0;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  flex-grow: 1;
  gap: ${({ theme }) => theme.space.s4} 0;
`

const PrimaryActions = styled(Actions)<{ $useMobileMenu: boolean }>`
  ${({ theme }) => theme.mediaQuery.tablet} {
    ${({ $useMobileMenu }) =>
      $useMobileMenu &&
      css`
        display: none;
      `}
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    flex-direction: column-reverse;

    ${ButtonBox} {
      width: 100%;

      ${StyledButton} {
        width: 100%;
      }
    }
  }
`

const ActionsToggleButton = styled(Button)`
  display: none;
  ${({ theme }) => theme.mediaQuery.tablet} {
    display: initial;
  }
`

const MobileActions = styled.div<{ $show: boolean }>`
  display: none;
  box-shadow: ${({ theme }) => theme.shadow.top};
  background-color: ${({ theme }) => theme.color.background};

  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;

  ${({ theme }) => theme.mediaQuery.tablet} {
    display: ${({ $show }) => ($show ? 'block' : 'none')};
  }
`

const MobileActionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space.s4};

  background-color: ${({ theme }) => theme.color.actionBarBackground};
  padding: ${({ theme }) => theme.space.s16};
`

const ActionBarFC = ({
  i18n,
  actions,
  useMobileMenu = false,
  selection,
  leftSideComponent,
  hideActionBar = false,
  getActionBarRoot = getActionBarRootDefault,
  ...props
}: ActionBarProps & { i18n: I18nComponentType }) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
  const parentRef = useRef(document.createElement('div'))

  useEffect(() => {
    const actionBarRoot = getActionBarRoot && getActionBarRoot()
    actionBarRoot?.appendChild(parentRef.current)
    return () => {
      actionBarRoot?.removeChild(parentRef.current)
    }
  }, [])

  const component = (
    <ActionBarWrapper setHidden={hideActionBar}>
      <MobileActions aria-hidden $show={isMobileMenuOpen}>
        <MobileActionContent>{actions}</MobileActionContent>
      </MobileActions>
      <StyledActionBar {...props}>
        <ActionBarContentWrapper $useMobileMenu={useMobileMenu}>
          {leftSideComponent && <LeftSide>{leftSideComponent}</LeftSide>}
          {selection && (
            <LeftSideText>
              <SelectionNumber>{selection.count}</SelectionNumber>
              <SelectionText>{selection.text}</SelectionText>
            </LeftSideText>
          )}
          {actions && (
            <PrimaryActions $useMobileMenu={useMobileMenu}>
              {actions}
            </PrimaryActions>
          )}
          {useMobileMenu && (
            <>
              <ActionsToggleButton
                aria-hidden
                variant={isMobileMenuOpen ? 'secondary' : 'primary'}
                icon={
                  isMobileMenuOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_up'
                }
                text={
                  isMobileMenuOpen
                    ? i18n.actionBar.hideActions
                    : i18n.actionBar.actions
                }
                onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              />
            </>
          )}
        </ActionBarContentWrapper>
      </StyledActionBar>
    </ActionBarWrapper>
  )

  return parentRef.current
    ? ReactDOM.createPortal(component, parentRef.current)
    : null
}

export const ActionBar = connect<{ i18n: I18nComponentType }>(
  mapLanguageToComponentProps()
)(ActionBarFC)
